import User from './User';
import Venue from './Venue';

export enum ReceiptStatus {
  DRAFT = 'DRAFT',
  CANCELLED = 'CANCELLED',
  CONFIRMED = 'CONFIRMED',
}
export enum StockProductType {
  FINISHED = 'FINISHED',
  RAW_MATERIAL = 'RAW_MATERIAL',
}
export enum StockOriginType {
  SUPPLIER = 'SUPPLIER',
  PRODUCTION_CENTER = 'PRODUCTION_CENTER',
}
export enum StockWarehouseType {
  CENTRAL = 'CENTRAL',
  INTERMEDIATE = 'INTERMEDIATE',
}
export enum ProductionCenterType {
  INTERNAL = 'INTERNAL',
  EXTERNAL = 'EXTERNAL',
}
export enum UnitOfMeasure {
  UNIT = 'UNIT',
  KG = 'KG',
  LITER = 'LITER',
  GRAM = 'GRAM',
  MILILITER = 'MILILITER',
}

export interface StockReception {
  id: string;
  venueId: string;
  receiptFileUrl?: string | null;
  purchaseOrderFileUrl?: string | null;
  observations: string;
  receivedAt: string;
  totalAmount: number;
  status: ReceiptStatus;

  originType: StockOriginType;
  originSupplierId: number | null;
  originProductionCenterId: string | null;
  destinationWarehouseId: string;
  originSupplier?: StockSupplier;
  originProductionCenter?: StockProductionCenter;

  items: StockReceptionItem[];
  destinationWarehouse?: StockWarehouse;
}

export interface StockReceptionItem {
  id: string;
  name: string;
  quantity: number;
  unitCost: number;
  type: StockProductType;
  sku: string | null;
  globalProductId: number | null;
  // Unidad de consumo (ingrediente en receta). Solo aplica a materias primas (RAW_MATERIAL).
  unitMeasure?: UnitOfMeasure | null;
  // Unidad de recepción/stock (cómo se compra/cuenta: botella=UNIT, paquete=UNIT).
  receptionUnitMeasure?: UnitOfMeasure | null;
  // Cuántas unidades de consumo hay en 1 unidad de recepción.
  unitsPerReceptionUnit?: number | null;
}

export interface StockReceiptPayload {
  venueId: string;
  receivedAt: string;

  originType: StockOriginType;
  originSupplierId: number | null;
  originProductionCenterId: string | null;
  destinationWarehouseId: string;

  receiptFile?: File | null;
  purchaseOrderFile?: File | null;

  observations?: string;

  products: StockReceptionItem[];
}

export interface StockWarehouse {
  id: string;
  name: string;
  type: StockWarehouseType;
  isActive: boolean;
  venueId: string;
  managerUserId: string | null;
  venue?: Venue;
  editor?: User;
}

// production center
export interface StockProductionCenter {
  id: string;
  name: string;
  venueId: string;
  type: ProductionCenterType;
  isActive: boolean;
}

// stock supplier
export interface StockSupplier {
  id: number;
  businessName: string;
  taxId: string;
  address: string;
  phone: string;
  email: string;
  isActive: boolean;
  venues?: Venue[];
}

// GlobalProduct
export interface GlobalProduct {
  id: number;
  sku: string;
  name: string;
  type: StockProductType;
  venueId: string;
  averageCost: number;
  replacementCost?: number | null;
  minimumStock?: number | null;
  safetyStock?: number | null;
  // unitMeasure = unidad de CONSUMO del ingrediente en la receta (p.ej. LITER para 0.5L de una botella de 2.5L).
  // NO es la unidad con la que se recibe/cuenta el stock (ver receptionUnitMeasure).
  unitMeasure?: UnitOfMeasure | null;
  // receptionUnitMeasure = unidad de RECEPCIÓN/stock: cómo se compra/cuenta (botella=UNIT, paquete=UNIT).
  receptionUnitMeasure?: UnitOfMeasure | null;
  // unitsPerReceptionUnit = cuántas unidades de consumo hay en 1 unidad de recepción.
  // Ej: botella 2.5L → unitMeasure=LITER, receptionUnitMeasure=UNIT, unitsPerReceptionUnit=2.5.
  unitsPerReceptionUnit?: number | null;
  // isUnitsLocked = true cuando el producto está en una receta o posee stock:
  // en ese caso las unidades de medida no pueden modificarse (read-only).
  isUnitsLocked?: boolean;
  image?: string | null;
}

export interface LocationProduct {
  commerceProductId?: string; // products.products
  globalProductId: number; // events.global_products
  sku: string;
  name: string;
  type: StockProductType;
  stock: number;
}

export interface StockCatalogLocation {
  id: string;
  name: string;
  stock: number;
}

export interface StockCatalogProduct {
  globalProductId: number;

  sku: string;
  averageCost: number;
  name: string;
  type: StockProductType;

  // Unidades del producto (para precargar/parsear en recepción de producto existente).
  unitMeasure?: UnitOfMeasure | null;
  receptionUnitMeasure?: UnitOfMeasure | null;
  unitsPerReceptionUnit?: number | null;

  warehouses: StockCatalogLocation[];

  commerces: StockCatalogLocation[];

  totalWarehouseStock: number;

  totalCommerceStock: number;

  totalStock: number;

  minimumStock: number | null;

  safetyStock: number | null;
}

export interface StockCatalogPagination {
  currentPage: number;
  perPage: number;
  total: number;
}

export interface StockCatalogResponse {
  totalValueInventoryCpp: number;
  products: StockCatalogProduct[];
  pagination: StockCatalogPagination;
  alertCount: number;
  criticalCount: number;
  minimumCount: number;
  okCount: number;
}

export interface StockWarehousePayload {
  venueId: string;
  name: string;
  type: StockWarehouseType;
  managerUserId?: string | null;
  isActive: boolean;
}
